import Compressor from 'compressorjs';

/**
 * Compressing and resizing and image based on the input maximum directional pixels.
 * @param file
 * @param width
 * @param height
 * @param maximumDirectionalPixels
 */
const compressFile = (file: File, width: number, height: number, maximumDirectionalPixels?: number) => {
	return new Promise<File | Blob>((resolve, reject) => {
		new Compressor(file, {
			quality: .9,
			...calculateImageDimensions(width, height, maximumDirectionalPixels),
			success: resolve,
			error: reject,
		})
	});
}

/**
 * Generating an image file from the base 64 string, that allows us to get
 * the dimensions of the image.
 * @param base64
 */
export const getImageFromBase64 = (base64: string) => new Promise<HTMLImageElement>((resolve, reject) => {
	const image = new Image();
	image.onload = () => resolve(image);
	image.onerror = reject;
	image.src = base64;
});

/**
 * Calculating the images dimentions to fit within
 * the set maximum directional pixels
 * @param width
 * @param height
 * @param maximumDirectionalPixels
 */
export const calculateImageDimensions = (
	width: number,
	height: number,
	maximumDirectionalPixels: number = 3072
): {width: number; height: number;} => {
	const aspectRatio = height / width;
	const isWidthLongest = width > height;
	const newWidth = width > maximumDirectionalPixels ? maximumDirectionalPixels : width;
	const newHeight = newWidth * aspectRatio;

	return {
		width: newWidth,
		height: newHeight,
	};
};

const getBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = async () => {
			const result = reader.result as string || '';
			resolve(result);
		};
		reader.onerror = error => reject(error);
	});
};

/**
 * Compressing the image
 * @param base64
 * @param file
 */
const compressImage = (base64: string, file: File, maximumDirectionalPixels?: number) =>
	new Promise<string>((resolve, reject) => {
		getImageFromBase64(base64)
			.then(async image => {
				compressFile(file, image.width, image.height, maximumDirectionalPixels)
					.then((compressedFile: File | Blob) => {
						getBase64(compressedFile as File)
							.then(async compressedImage => {
								getImageFromBase64(compressedImage)
									.then((compressed) => {
										resolve(compressed?.src);
									})
							})
							.catch(reject)
					})
					.catch(reject)
			})
			.catch(reject);
	});

/**
 * Converting a image file into base64 with the file reader and compressing it if needed
 * @param file
 */
export const convertFileToBase64 = (file: File): Promise<string | ArrayBuffer> => {
	return new Promise((resolve, reject) => {
		getBase64(file)
			.then((result) => {
				const fileSize = file.size / 1024 / 1024;
				const eightMegaPixels = 3_264;
				if (fileSize > 2) {
					compressImage(result, file, eightMegaPixels)
						.then(resolve)
						.catch(reject);
				}
				else {
					resolve(result);
				}
			})
			.catch(reject);
	});
};

/**
 * Creating a file from a base 64 string
 * @param base64
 * @param fileName
 */
const convertDataUrlToFile = (base64: string, fileName: string): File | undefined => {
	const splitData = base64.split(',');
	if (splitData.length < 2) {
		return undefined;
	}
	const mime = splitData[0].match(/:(.*?);/);
	if (!mime || mime.length < 2) {
		return undefined;
	}
	const buffer = Buffer.from(splitData[1]);
	return new File([buffer], fileName, {
		type: mime[0],
	});
};
